/**
 * NCR Edit Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useNCREdit hook.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft, Loader2, Package, PackageCheck, CreditCard, FileWarning, RotateCcw,
  Lock, Unlock,
} from 'lucide-react';
import {
  useNCREdit,
  Template1ProgressBar,
  Template2ProgressBar,
  NCRDetailTab,
  RootCauseTab,
  CorrectiveActionTab,
  DocumentsSection,
  CommentsSection,
  ActionLogSection,
  CloseNCRDialog,
  RejectCARDialog,
  RejectNCRDialog,
  ReopenNCRDialog,
} from './ncr-edit';
import { CollectionWorkflowTab } from './ncr-edit/CollectionWorkflowTab';
import { CreditWorkflowTab } from './ncr-edit/CreditWorkflowTab';
import { ReplacementWorkflowTab } from './ncr-edit/ReplacementWorkflowTab';
import { platformSettingsAPI, QualityTabsVisibility } from '../lib/api';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { collectionBadgeFor, creditBadgeFor, replacementBadgeFor } from './ncr-edit/tabBadges';


export default function NCREdit() {
  const navigate = useNavigate();
  const {
    // Data
    ncr,
    documents,
    comments,
    actionLogs,
    formData,

    // UI states
    loading,
    saving,
    currentStep,
    ncrTemplate,
    is8DRequired,
    newComment,
    closeDialogOpen,

    // Permissions
    isCustomer,
    isSupplier,
    canActAsCustomer,
    canEditStep1,
    canEditStep2,
    canEditStep3,
    canInputCAR,

    // Customer users
    customerUsers,

    // Classification options
    sourcesOfRejection,
    commonisedActions,
    faultGroups,
    faultCategories,

    // CR-5999: per-field RCA validation errors surfaced inline next to each
    // required field on a failed Submit RCA attempt.
    rcaFieldErrors,

    // CR-6000: per-field CAR validation errors surfaced inline next to each
    // required field on a failed Submit CAR attempt.
    carFieldErrors,

    // Setters
    setNewComment,
    setCloseDialogOpen,
    // CR-6024
    rejectCARDialogOpen,
    setRejectCARDialogOpen,
    handleRejectCAR,
    // CR-6024 r3: reopen-closed-NCR
    reopenDialogOpen,
    setReopenDialogOpen,
    handleReopen,
    // Supplier reject-NCR + customer approve/decline
    rejectNCRDialogOpen,
    setRejectNCRDialogOpen,
    handleRejectNCR,
    handleApproveRejection,
    handleDeclineRejection,
    // 8D scoring: customer CAR grades captured with the approve/rework decision
    rcScore,
    caScore,
    handleRcScoreChange,
    handleCaScoreChange,
    updateFormData,

    // Actions
    refreshData,
    goBack,
    handleSave,
    handleSubmitDetail,
    handleSubmitRCA,
    handleSubmitCAR,
    handleAddComment,
    handleClose,
    getTemplate2Steps,

    // Multi-step workflow
    handleInitiateCollection,
    handleUpdateCollectionStatus,
    handleInitiateCredit,
    handleSubmitCreditNote,
    handleUpdateCreditStatus,
    handleInitiateReplacement,
    handleSubmitReplacementDispatch,
    handleUpdateReplacementStatus,

    // Computed
    canClose,
  } = useNCREdit();

  // Schedule-lines tenant flag gates the delivery Warehouse field on the NCR
  // detail page. Reuses the established PlatformSettings.dashboard_tabs
  // 'scheduled' flag exposed via MenuSettingsContext — the same flag that
  // toggles the scheduled-deliveries dashboard tab. Warehouse on the GRN is
  // only meaningful for tenants receiving against scheduled deliveries.
  const { isDashboardTabEnabled } = useMenuSettings();
  const showWarehouse = isDashboardTabEnabled('scheduled');

  // Master Edit — an administrative override letting a customer admin amend an
  // NCR after it has left draft, at any workflow stage. Normal stage gating
  // (canEditStep1/2/3) is what a supplier and a rank-and-file customer user
  // get; this sits on top of it for admins only.
  //
  // The backend enforces the same rule and audits every changed field to the
  // NCR action log, so this toggle is an affordance, not the security boundary.
  const { user } = useAuth();
  const canMasterEdit =
    (isCustomer && !!user?.is_company_admin) || !!user?.is_staff || !!user?.is_superuser;
  const [masterEditing, setMasterEditing] = useState(false);
  // Never leave the override armed on a draft — draft is already editable, so
  // the toggle would be meaningless and the banner misleading.
  const masterEditActive = canMasterEdit && masterEditing && ncr?.status !== 'draft';

  // Tab visibility check
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // Check if NCRs tab is enabled
  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.quality_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabSettingsLoaded(true));
  }, []);

  // Redirect if NCRs tab is disabled
  useEffect(() => {
    if (!tabSettingsLoaded) return;
    if (!tabVisibility.ncrs) {
      navigate('/quality', { replace: true });
    }
  }, [tabSettingsLoaded, tabVisibility.ncrs, navigate]);

  // Show loading while checking tab settings or loading NCR
  if (!tabSettingsLoaded || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (!ncr) {
    return (
      <div className="p-6">
        <div className="text-center">NCR not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{ncr.ncr_number}</h1>
            {/* Was "NCR ID: {ncr.id}" — the database primary key. That is an
                  implementation detail: not stable across a restore, meaningless
                  in the customer's own systems, and its range hints at how many
                  NCRs exist platform-wide. Numbers are assigned on creation now,
                  so the heading above carries the identity. */}
              <p className="text-muted-foreground">Non-Conformance Report</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* CR-6024 r3: customer-only Reopen affordance on a closed NCR. Spec
              acceptance criterion: "Closed NCRs can be reopened by customer
              users if required."
              r4: gate on canActAsCustomer (excludes Helix admin) so the button
              only renders when the backend will actually accept the action —
              Helix admins must Login As to reopen. */}
          {canActAsCustomer && ncr.status === 'closed' && (
            <Button type="button" variant="outline" size="sm" onClick={() => setReopenDialogOpen(true)}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reopen NCR
            </Button>
          )}
          {/* Supplier: reject a submitted (open) NCR with a free-text reason.
              Hidden once a rejection is already pending or the NCR is closed. */}
          {isSupplier
            && ['created', 'ca_submitted', 'car_submitted'].includes(ncr.status)
            && !ncr.is_rejected && (
            <Button type="button" variant="destructive" size="sm" onClick={() => setRejectNCRDialogOpen(true)}>
              Reject NCR
            </Button>
          )}
          {ncr.is_rejected && (
            <Badge variant="destructive">Rejection requested</Badge>
          )}
          <Badge variant={ncr.status === 'closed' ? 'default' : 'secondary'}>
            {ncr.status_display}
          </Badge>
          {/* Master Edit: only offered once the NCR has left draft, since a
              draft is already editable through the normal flow. */}
          {canMasterEdit && ncr.status !== 'draft' && (
            <Button
              type="button"
              variant={masterEditing ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setMasterEditing((v) => !v)}
            >
              {masterEditing ? (
                <><Unlock className="h-4 w-4 mr-2" />Master Edit On</>
              ) : (
                <><Lock className="h-4 w-4 mr-2" />Master Edit</>
              )}
            </Button>
          )}
        </div>
      </div>

      {masterEditActive && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">Master Edit is on</p>
          <p className="mt-1 text-sm text-amber-800">
            You are amending a submitted NCR as an administrator. Every field you
            change is recorded in the Workflow Action Log below, with the previous
            and new value against your name.
          </p>
        </div>
      )}

      {/* Customer: review a supplier's NCR rejection request. Approving voids
          the NCR (backend sets status → deleted) and records the reason in the
          audit history; declining lets the NCR continue. */}
      {canActAsCustomer && ncr.is_rejected && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800">Supplier has rejected this NCR</p>
          <p className="mt-1 text-sm text-red-700 whitespace-pre-wrap">
            <span className="font-medium">Reason: </span>{ncr.rejection_reason || '—'}
          </p>
          <div className="mt-3 flex gap-2">
            <Button type="button" variant="destructive" size="sm" onClick={handleApproveRejection}>
              Accept &amp; Void NCR
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleDeclineRejection}>
              Decline
            </Button>
          </div>
        </div>
      )}

      {/* CR-5981 rework: the 4-stage NCR workflow stepper (Created >
          CA Submitted > CAR Submitted > Closed) sits at the top of the
          NCR record above the detail panel — mirrors the legacy
          wizard-nav header. Shown on both templates. */}
      <Template1ProgressBar currentStep={currentStep} />

      {/* NCR Details - Always visible at top */}
      <NCRDetailTab
        ncr={ncr}
        formData={formData}
        canEdit={canEditStep1 || masterEditActive}
        saving={saving}
        customerUsers={customerUsers}
        sourcesOfRejection={sourcesOfRejection}
        faultGroups={faultGroups}
        faultCategories={faultCategories}
        showWarehouse={showWarehouse}
        canConfirmContainment={canActAsCustomer}
        onUpdateFormData={updateFormData}
        onSave={handleSave}
        onSubmitDetail={handleSubmitDetail}
        onCloseNo8D={() => setCloseDialogOpen(true)}
      />

      {/* Workflow Tabs: 8D Report, Collection, Credit, Replacement */}
      <Tabs defaultValue="8d_report" className="space-y-4">
        <TabsList className={`grid w-full ${ncr.is_replacement_required ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <TabsTrigger value="8d_report" className="flex items-center gap-2">
            <FileWarning className="h-4 w-4" />
            8D Report
          </TabsTrigger>
          <TabsTrigger value="collection" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Collection
            {ncr.is_collection_required && (() => {
              const b = collectionBadgeFor(ncr);
              return (
                <Badge variant={b.variant} className="ml-1 text-[10px] px-1.5 py-0">
                  {b.label}
                </Badge>
              );
            })()}
          </TabsTrigger>
          <TabsTrigger value="credit" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Credit
            {ncr.is_credit_required && (() => {
              const b = creditBadgeFor(ncr);
              return (
                <Badge variant={b.variant} className="ml-1 text-[10px] px-1.5 py-0">
                  {b.label}
                </Badge>
              );
            })()}
          </TabsTrigger>
          {/* Replacement tab — enabled when the claim needs replacement parts,
              mirroring the Credit tab (spec: "similar to credits"). */}
          {ncr.is_replacement_required && (
            <TabsTrigger value="replacement" className="flex items-center gap-2">
              <PackageCheck className="h-4 w-4" />
              Replacement
              {(() => {
                const b = replacementBadgeFor(ncr);
                return (
                  <Badge variant={b.variant} className="ml-1 text-[10px] px-1.5 py-0">
                    {b.label}
                  </Badge>
                );
              })()}
            </TabsTrigger>
          )}
        </TabsList>

        {/* CR-5981 rework: 8D-specific stepper below the 8D/Collection/Credit
            tab row — Template 2 only. Visualises the dynamic
            Liability / 8D / Collection / Credit progression with the
            "8D Completed" step gated on is_8d_required (mirrors the
            legacy "8D PROCESS" card). Template 1 has mandatory 8D and
            no separate 8D process card in legacy, so showing a second
            stepper here would just duplicate the one at the top. */}
        {ncrTemplate === 2 && (
          <Template2ProgressBar steps={getTemplate2Steps()} compact />
        )}

        {/* 8D Report Tab — Root Cause + Corrective Action exposed as nested
            sub-tabs (customer request) instead of stacked sections.
            Default sub-tab follows the NCR workflow stage so the user lands
            on the section they're most likely to be working on:
              draft / ca_submitted → Root Cause (RCA in progress)
              car_submitted / closed → Corrective Action (RCA already done) */}
        <TabsContent value="8d_report" className="space-y-4">
          {!is8DRequired ? (
            // 8D Required = No: the supplier must not see the Root Cause
            // Analysis or Corrective & Preventive Action fields. Mirror the
            // Collection/Credit "not required" empty-state instead.
            <div className="rounded-lg border bg-card p-8 text-center">
              <FileWarning className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                An 8D report is not required for this NCR. Root cause analysis and
                corrective &amp; preventive actions are not needed.
              </p>
            </div>
          ) : (
          <Tabs
            defaultValue={
              ['car_submitted', 'closed'].includes(ncr.status) ? 'corrective_action' : 'root_cause'
            }
            className="space-y-4"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="root_cause">Root Cause Analysis</TabsTrigger>
              <TabsTrigger value="corrective_action">Corrective Action Report</TabsTrigger>
            </TabsList>
            <TabsContent value="root_cause">
              <RootCauseTab
                formData={formData}
                canEdit={canEditStep2 || masterEditActive}
                saving={saving}
                fieldErrors={rcaFieldErrors}
                // Master override: authorised internal (customer / Helix
                // admin) users may unlock and edit the supplier-only RCA
                // section through the portal. Saves via the standard NCR
                // PATCH (no workflow transition), which is permitted for
                // customer / helix users by the viewset's company scope.
                // The supplier's normal edit flow (canEdit) is unaffected.
                isInternal={!!ncr.is_internal}
                onUpdateFormData={updateFormData}
                onSave={handleSave}
                onSubmitRCA={handleSubmitRCA}
              />
            </TabsContent>
            <TabsContent value="corrective_action">
              <CorrectiveActionTab
                formData={formData}
                // Supplier can input/save the CAR alongside the RCA (status
                // 'created'); submitting the CAR still requires 'ca_submitted'.
                canEdit={canInputCAR || masterEditActive}
                canSubmit={canEditStep3}
                isInternal={!!ncr.is_internal}
                saving={saving}
                fieldErrors={carFieldErrors}
                onUpdateFormData={updateFormData}
                onSave={handleSave}
                onSubmitCAR={handleSubmitCAR}
                // CR-6024 r2: render the customer Approve / Reject & Rework
                // controls inside the CAR card (directly under the content the
                // customer is reviewing), per Jacq's rework note. canClose
                // already covers status==car_submitted + any collection/credit
                // gating.
                // DMR 71216: review cluster shows as soon as the CAR is
                // submitted — Reject & Rework must not wait for the claim
                // workflows; only Approve (which closes the NCR) is gated.
                showCustomerActions={canActAsCustomer && ncr.status === 'car_submitted'}
                canApprove={canClose}
                onApproveClick={() => setCloseDialogOpen(true)}
                onRejectClick={() => setRejectCARDialogOpen(true)}
                commonisedActions={commonisedActions}
                rcScore={rcScore}
                caScore={caScore}
                onRcScoreChange={handleRcScoreChange}
                onCaScoreChange={handleCaScoreChange}
              />
            </TabsContent>
          </Tabs>
          )}
        </TabsContent>

        {/* Collection Tab */}
        <TabsContent value="collection">
          <CollectionWorkflowTab
            ncr={ncr}
            isCustomer={isCustomer}
            isSupplier={!isCustomer}
            saving={saving}
            onInitiate={handleInitiateCollection}
            onUpdateStatus={handleUpdateCollectionStatus}
          />
        </TabsContent>

        {/* Credit Tab */}
        <TabsContent value="credit">
          <CreditWorkflowTab
            ncr={ncr}
            isCustomer={isCustomer}
            isSupplier={!isCustomer}
            saving={saving}
            onInitiate={handleInitiateCredit}
            onSubmitNote={handleSubmitCreditNote}
            onUpdateStatus={handleUpdateCreditStatus}
          />
        </TabsContent>

        {/* Replacement Tab (mirrors Credit) */}
        <TabsContent value="replacement">
          <ReplacementWorkflowTab
            ncr={ncr}
            isCustomer={isCustomer}
            isSupplier={!isCustomer}
            saving={saving}
            onInitiate={handleInitiateReplacement}
            onSubmitDispatch={handleSubmitReplacementDispatch}
            onUpdateStatus={handleUpdateReplacementStatus}
          />
        </TabsContent>
      </Tabs>

      {/* Documents */}
      <DocumentsSection ncrId={ncr.id} documents={documents} onDocumentsChanged={refreshData} />

      {/* Communications */}
      <CommentsSection
        comments={comments}
        newComment={newComment}
        onNewCommentChange={setNewComment}
        onAddComment={handleAddComment}
      />

      {/* Action Log */}
      <ActionLogSection actionLogs={actionLogs} />

      {/* CR-6024 r2: the Approve / Reject & Rework buttons that used to live
          here at the bottom of the page were moved up into CorrectiveActionTab
          so they sit directly under the CAR the customer is reviewing (Jacq
          rework note). Only the dialogs remain here. */}

      {/* Approve confirmation (closes the NCR) */}
      <CloseNCRDialog
        open={closeDialogOpen}
        onOpenChange={setCloseDialogOpen}
        onClose={handleClose}
      />

      {/* CR-6024: Reject & Rework reason modal */}
      <RejectCARDialog
        open={rejectCARDialogOpen}
        onOpenChange={setRejectCARDialogOpen}
        onReject={handleRejectCAR}
      />

      {/* CR-6024 r3: Reopen NCR confirmation modal */}
      <ReopenNCRDialog
        open={reopenDialogOpen}
        onOpenChange={setReopenDialogOpen}
        onReopen={handleReopen}
      />

      {/* Supplier reject-NCR reason modal */}
      <RejectNCRDialog
        open={rejectNCRDialogOpen}
        onOpenChange={setRejectNCRDialogOpen}
        onReject={handleRejectNCR}
      />
    </div>
  );
}
